import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SockJS from "sockjs-client";
import { Client, over } from "stompjs";
import { useTranslation } from "react-i18next";

import Button from "../../components/atoms/button";
import Modal from "../../components/atoms/modal";

import ConfirmSubmissionModal from "./_components/confirmSubmissionModal";
import AssessmentDashboard from "./assessmentDashboard";
import Question from "./question";

import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { useLazyGetAssessmentOngoingQuery } from "../../store/slices/api/assessmentApiSlice";

import GetHeroIcon from "../../utils/getHeroIcons";
import { sequentializeList } from "../../utils/sequentializeList";
import { ParseAssessmentSectionsList } from "../../utils/parseAssessmentSectionsList";

import { OngoingSection } from "../../types/assessmentOngoing";
import AssessmentTimer from "./_components/assessmentTimer";
import { useShowErrors } from "./_components/toasterWrapper";
import { StompAssessProvider, useAssessStomp } from "@lxp-app/stompAssessment";
import ErrorListAlerts from "@lxp-app/pages/event-creation-workflow/errorListAlert";
import orgBasedFeatureNameMapping from "@assessment/utils/orgBasedFeatureName";

const Assessment = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { scheduleId } = useParams();

  const showErrors = useShowErrors();

  const [output, setOutput] = useState("");
  const [isCodingQues, setIsCodingQues] = useState(false);
  const [online, setOnline] = useState(false);
  const [offline, setOffline] = useState(false);

  const stompClientRef = useRef<Client | null>(null);
  const subscribedIdRef = useRef<Client | null | string>(null);

  const [getAssessmentOngoing, { isFetching, isLoading, isError }] =
    useLazyGetAssessmentOngoingQuery();

  const ongoingId = useAppSelector((store) => store.AssessmentSlice.ongoingId);
  const user = useAppSelector((state) => state.UserInfoSlice.profile);

  const assessmentActiveView = useAppSelector(
    (store) => store.AssessmentSlice.assessmentActiveView
  );

  const activeQuestionMeta = useAppSelector(
    (store) => store.AssessmentSlice.activeQuestionMeta
  );
  const showSubmissionModal = useAppSelector(
    (store) => store.AssessmentSlice.modalFlags.showSubmissionsModal
  );

  const assessmentData = useAppSelector(
    (state) => state.AssessmentSlice.assessmentData
  );

  const ongoingQpDuration = assessmentData?.ongoingQpDuration;

  const giveShuffledOptionsForObjectiveQuestions = useAppSelector(
    (store) => store.AssessmentSlice.preAssessmentData?.randomization?.options
  );
  const isOnline = useAppSelector((store) => store.AssessmentSlice?.isOnline);

  const [timeSpentFns, setTimeSpentFns] = useState<{
    updateTimeSpentHandler: () => void;
    updateTimeSpentOnSection: () => void;
  } | null>(null);

  const orgBasedFeatureName =
    orgBasedFeatureNameMapping[(user?.org as string) || "default"] ||
    orgBasedFeatureNameMapping["default"];

  useEffect(() => {
    (async () => {
      try {
        if (!scheduleId) {
          throw {
            status: "",
            title: t("assessment.scheduleIdNotFound"),
            err: {},
            description: ""
          };
        }
        if (!ongoingId) {
          throw {
            status: "",
            title: t("assessment.ongoingIdNotFound"),
            err: {},
            description: ""
          };
        }

        const raw = await getAssessmentOngoing({
          ongoingId: ongoingId,
          scheduleId: scheduleId
        });

        if (raw.error) {
          throw {
            status: raw.status,
            title: t("assessment.errorFetchingOngoingData"),
            err: raw.error,
            description: ""
          };
        }

        if (raw.isSuccess) {
          const data = raw.data.data;
          if (data.ongoingQpDuration) {
            dispatch(
              AssessmentSliceActions.mutateTimeLeft(data?.ongoingQpDuration)
            );
          }
          const sequentializeOngoingSections = sequentializeList(
            data.ongoingSections,
            "sections"
          );
          const sequentizeQuestionsInsideOngoingSections =
            sequentializeOngoingSections.map((section) => {
              const curr = section as OngoingSection;
              const sequentializedQuestions = sequentializeList(
                curr.questions,
                "questions"
              );
              if (section?.questionTypeCountMap?.coding > 0)
                setIsCodingQues(true);
              return { ...curr, questions: sequentializedQuestions };
            });

          const payload = {
            ...data,
            ongoingSections: sequentizeQuestionsInsideOngoingSections
          };
          const sections: unknown = sequentializeOngoingSections;
          const {
            assessmentProgressPayload,
            candidateSolutionsPayload,
            candidateFibSolutionsPayload,
            assessmentTimeSpent,
            shuffledOptionsForQuestions
          } = ParseAssessmentSectionsList(
            sections as OngoingSection[],
            !!giveShuffledOptionsForObjectiveQuestions
          );

          if (giveShuffledOptionsForObjectiveQuestions) {
            dispatch(
              AssessmentSliceActions.mutateShuffledOptionsForObjectiveQuestions(
                shuffledOptionsForQuestions
              )
            );
          }

          dispatch(
            AssessmentSliceActions.mutateAssessmentProgress(
              assessmentProgressPayload
            )
          );
          dispatch(
            AssessmentSliceActions.mutateCandidateResponseBulk(
              candidateSolutionsPayload
            )
          );
          dispatch(
            AssessmentSliceActions.mutateCandidateFibResponseBulk(
              candidateFibSolutionsPayload
            )
          );
          dispatch(
            AssessmentSliceActions.mutateTimeSpentBulk(assessmentTimeSpent)
          );
          dispatch(AssessmentSliceActions.mutateAssessmentData(payload));
        }
      } catch (err) {
        console.error(err);
        const temp: any = err;
        const rawErrList = temp?.err?.data?.error;
        const errList =
          rawErrList?.length > 0
            ? rawErrList?.map((el: string) => el.split("_")[0])
            : [];
        if (showErrors)
          showErrors(temp?.title, errList, "error", temp?.description);
      }
    })();
  }, []);

  const { setStompClient, setIsConnected } = useAssessStomp();
  const timesUpModalOpen = useAppSelector(
    (state) => state.AssessmentSlice.timesUpModalOpen
  );
  const view = useAppSelector(
    (state) => state.AssessmentSlice.assessmentActiveView
  );

  const reconnectTimer = useRef<NodeJS.Timeout | null>(null);
  const stompRef = useRef<Client | null>(null);

  // useEffect(() => {
  //   const socket = new SockJS(`${import.meta.env.VITE_CODING_WEBSOCKET_URL}`);
  //   const stomp = over(socket);

  //   stomp.connect({}, () => {
  //     setIsConnected(true);
  //     setStompClient(stomp);
  //     console.log("Connected");
  //   });

  //   return () => {
  //     if (stomp.connected) {
  //       stomp.disconnect(() => console.log("Disconnected"));
  //     }
  //   };
  // }, [setIsConnected, setStompClient]);

  const connect = () => {
    const socket = new SockJS(import.meta.env.VITE_CODING_WEBSOCKET_URL);
    const stomp = over(socket);

    stomp.connect(
      {},
      () => {
        console.log("Connected");
        setIsConnected(true);
        setStompClient(stomp);
        stompRef.current = stomp;
      },
      (error) => {
        console.warn("Connection error:", error);
        setIsConnected(false);
        retryConnect();
      }
    );
  };

  const retryConnect = () => {
    if (reconnectTimer.current) return;
    reconnectTimer.current = setTimeout(() => {
      console.log("Retrying STOMP connection");
      connect();
      reconnectTimer.current = null;
    }, 10000);
  };

  useEffect(() => {
    if (isCodingQues) {
      connect();
    }
  }, [isCodingQues]);

  useEffect(() => {
    if (isOnline && isCodingQues) {
      if (!stompRef.current?.connected) connect();
      else {
        setOffline(false);
        setOnline(true);
      }
    }
    if (!isOnline && isCodingQues) {
      setOffline(true);
      setOnline(false);
    }
  }, [isOnline]);

  useEffect(() => {
    if (timesUpModalOpen && timeSpentFns && view) {
      if (view == "question") {
        timeSpentFns.updateTimeSpentHandler();
        timeSpentFns.updateTimeSpentOnSection();
      }
    }
  }, [timesUpModalOpen, timeSpentFns, view]);

  if (isFetching || isLoading) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-screen gap-5">
        <div className="w-8 h-8 border-t rounded-full border-t-primary border-secondary animate-spin" />
        <span>{t("common.loading")}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-screen gap-5">
        <GetHeroIcon
          name="ExclamationTriangleIcon"
          className="w-6 h-6"
          variant="outline"
        />
        <span>
          {t("assessment.fetchError", { featureName: orgBasedFeatureName })}
        </span>
      </div>
    );
  }

  return (
    <div>
      {/* <button onClick={send}>Send</button> */}
      {/* position will center till lg, at right till 500px, after 500px it will
      take full width and position at right */}
      {ongoingQpDuration && (
        <div className="min-[500px]:absolute lg:mt-8 mt-6 max-[500px]:mt-10 max-[500px]:flex max-[500px]:justify-center max-[500px]:mr-8 max-[500px]:mb-5 lg:left-1/2 lg:transform lg:-translate-x-1/2 max-lg:right-10">
          <AssessmentTimer qpDuration={ongoingQpDuration} />
        </div>
      )}
      {assessmentActiveView === "dashboard" && (
        <div className="min-[500px]:p-8 max-[500px]:px-8 lg:px-14">
          <AssessmentDashboard />
        </div>
      )}
      {assessmentActiveView === "question" && <Question />}
      {assessmentActiveView === "question" && activeQuestionMeta === null && (
        <div className="flex flex-col items-center justify-center gap-3 mt-20">
          <GetHeroIcon
            name="ExclamationTriangleIcon"
            variant="outline"
            className="w-10 h-10"
          />
          <span className="text-base">
            {t("assessment.somethingWentWrong")}
          </span>
          <Button
            type="button"
            variant="contained"
            buttonColor="secondary"
            className="mt-10"
            onClick={() => {
              dispatch(
                AssessmentSliceActions.mutateAssessmentView("dashboard")
              );
              dispatch(AssessmentSliceActions.mutateActiveQuestionMeta(null));
            }}
          >
            {t("assessment.goBackToDashboard")}
          </Button>
        </div>
      )}
      <Modal
        open={showSubmissionModal}
        setOpen={() => {
          dispatch(AssessmentSliceActions.mutateShowSubmissionModal(false));
        }}
        dialogueClassName="w-fit p-8"
        childDialogTitle={t("assessment.confirmSubmissionTitle")}
        closeBtn
      >
        <ConfirmSubmissionModal />
      </Modal>

      {online && (
        <ErrorListAlerts
          severity="success"
          label={t("assessment.connectionRestoredLabel")}
          description={t("assessment.connectionRestoredDescription")}
          messageList={[]}
          autoHideDuration={5000}
        />
      )}
      {offline && (
        <ErrorListAlerts
          severity="error"
          label={t("assessment.connectionLostLabel")}
          description={t("assessment.connectionLostDescription")}
          messageList={[]}
          autoHideDuration={5000}
        />
      )}
    </div>
  );
};

export default Assessment;
